// Umami analytics — visitors, where they are, how long they stay, and which
// layers they actually switch on. Self-hosted (see docs/DEPLOYMENT.md), so the
// numbers live in our Postgres and go nowhere else.
//
// Wholly inert unless BOTH build-time variables are set:
//
//   VITE_UMAMI_URL         the instance origin, e.g. https://stats.scot.live
//   VITE_UMAMI_WEBSITE_ID  the UUID Umami assigns when a website is registered
//
// Unset — every dev build, and any deployment that has not opted in — this
// module injects nothing, loads nothing, and track() is a no-op. That is why
// the script tag is added here rather than written into index.html: an
// unconditional tag would point every checkout at production stats, and a
// hardcoded website id would count Aberdeen and Edinburgh as one site when the
// whole point of registering them separately is telling them apart.
//
// Umami is cookieless — visitors are a salted daily hash, so there is nothing
// here that needs a consent banner under PECR. Do not "improve" this by adding
// localStorage ids; the privacy note in the report dialog says nothing else
// about visitors is stored, and it has to stay true.
//
// The instance origin also has to be allowed by the CSP in public/_headers
// (script-src for the loader, connect-src for the /api/send beacon). A blocked
// origin fails silently here by design — the console names it, the map works.

const URL_ = import.meta.env.VITE_UMAMI_URL || '';
const WEBSITE_ID = import.meta.env.VITE_UMAMI_WEBSITE_ID || '';

/** What the Umami loader puts on window once it has arrived. */
declare global {
  interface Window {
    umami?: { track: (event: string, data?: Record<string, string | number | boolean>) => void };
  }
}

/**
 * Inject the tracker. Call once, early, from each page entry that should be
 * counted. Pageviews, referrers, visit duration and geography all come from
 * this one script; nothing else in the app needs to do anything.
 */
export function initAnalytics(): void {
  if (!URL_ || !WEBSITE_ID) return;

  const s = document.createElement('script');
  s.defer = true;
  s.src = `${URL_.replace(/\/$/, '')}/script.js`;
  s.dataset.websiteId = WEBSITE_ID;
  document.head.appendChild(s);
}

/**
 * Record a named event with optional properties — which layer was toggled,
 * which panel was docked. Safe to call always: before the loader arrives,
 * with analytics unconfigured, or with the script eaten by a blocker, it
 * simply does nothing. Losing an event is fine; breaking a toggle is not.
 */
export function track(event: string, data?: Record<string, string | number | boolean>): void {
  try {
    window.umami?.track(event, data);
  } catch {
    // An analytics failure must never surface in the UI.
  }
}
